import React, { useEffect, useRef, useState } from 'react';
import { TableRow } from '@material-ui/core';
import { useIntl } from 'react-intl';
import moment from 'moment';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { StyledTableCellProps as StyledTableCell, StyledTooltip } from '../../commons/utils/TableStyles';
import Security from '../../commons/security/Security.js';
import Timezone from '../../commons/timezone/Timezone.js';
import LocaleFormat from '../../commons/utils/LocaleFormat.js';
import * as Utils from '../../commons/utils/Utils';
import swal2 from 'sweetalert2';
import backendStrings from '../../lang/backendStrings.js';

const DisponibilidadActivaGridRow = ({ movil, comboMotivos, onActualizarMotivo, onActualizarFechaRetorno, columnsEnabled, seleccionable, seleccionado, onToggleSeleccion }) => {
  const intl = useIntl();
  const refDropdown = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (refDropdown.current && !refDropdown.current.contains(event.target)) {
        // Bootstrap dropdown handles its own close; this ref is kept for future use
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getNombreMotivo = (id) => {
    const found = comboMotivos.filter(m => m.id === id);
    if (found.length === 0) {
      return intl.formatMessage({ id: 'disponibilidadGridRow_sin_motivo', defaultMessage: 'Sin motivo' });
    }
    const motivo = found[0];
    if (motivo.multilenguaje_id && backendStrings[motivo.multilenguaje_id]) {
      return intl.formatMessage(backendStrings[motivo.multilenguaje_id]);
    }
    return motivo.nombre;
  };

  const handleChangeMotivo = (motivoId) => {
    Utils.sendData('/disponibilidad/activa/motivo', 'PUT', {
      motivo_id: motivoId,
      movil_historial_ocioso_v2_id: movil.id,
    }).then(({ motivo_id, id }) => {
      onActualizarMotivo(motivo_id, id);
      swal2.fire(
        intl.formatMessage({ id: 'El_cambio_fue_guardado_con_Exito!', defaultMessage: 'El cambio fue guardado con Éxito!' }),
        ' ',
        'success'
      );
    });
  };

  // VEC-3894: dato puramente informativo, sin relación con el motivo — endpoint propio,
  // clavado por movil_historial_ocioso_v2_id. A diferencia de V1 (que setea el estado local
  // ANTES de la respuesta, fire-and-forget), acá se espera el `.then()` y se parchea el estado
  // vía el callback del padre recién con la confirmación del backend: como este endpoint SÍ
  // valida (formato + piso en fecha_inicio_ociosidad), un 400 no debe dejar al usuario creyendo
  // que guardó. Si el PUT falla, Utils.sendData ya dispara el alert de error (handleError) y
  // este `.then()` nunca corre — la celda simplemente no cambia.
  const handleChangeFechaRetorno = (date) => {
    Utils.sendData('/disponibilidad/activa/fecha-retorno', 'PUT', {
      movil_historial_ocioso_v2_id: movil.id,
      fecha_retorno: date ? date.format('YYYY-MM-DD') : null,
    }).then(({ fecha_retorno, id }) => {
      onActualizarFechaRetorno(fecha_retorno, id);
      swal2.fire(
        intl.formatMessage({ id: 'El_cambio_fue_guardado_con_Exito!', defaultMessage: 'El cambio fue guardado con Éxito!' }),
        ' ',
        'success'
      );
    });
  };

  const dropdownMotivos = () => {
    const tienePermiso = Security.hasPermission('DISPONIBILIDAD_ACTIVA_ASIGNAR_MOTIVOS');
    // VEC-3726: el valor de motivo se sigue mostrando siempre (más abajo, fuera de este bloque
    // condicional) — acá solo se decide si el control de edición (ícono + dropdown) aparece.
    // `!== false` en vez de `=== true`: si algún dato legacy no trae motivoEditable, no se
    // bloquea la edición por eso.
    const editable = tienePermiso && movil.motivoEditable !== false;

    const controlEdicion = (
      <div>
        <i
          id="dLabel"
          type="button"
          data-toggle="dropdown"
          aria-haspopup="true"
          aria-expanded="false"
          className="fa fa-chevron-down cursor-pointer"
          style={{ marginRight: '5px', marginLeft: '5px' }}
        />
        <div className="dropdown-menu" aria-labelledby="dLabel">
          <p
            key="ninguno"
            className="dropdown-item"
            style={{ cursor: 'pointer' }}
            onClick={() => handleChangeMotivo(null)}
          >
            {intl.formatMessage({ id: 'disponibilidad_ociosidad_seleccioneMotivo', defaultMessage: 'Seleccione Motivo' })}
          </p>
          {comboMotivos.map((motivo, i) => (
            <p
              key={i}
              className="dropdown-item"
              style={{ cursor: 'pointer' }}
              onClick={() => handleChangeMotivo(motivo.id)}
            >
              {motivo.nombre}
            </p>
          ))}
        </div>
      </div>
    );

    return (
      <div
        className="d-flex align-items-center"
        style={{ padding: '4px', backgroundSize: 'auto', width: 'fit-content' }}
      >
        <div>{getNombreMotivo(movil.motivo_id)}</div>
        {tienePermiso && !editable && (
          <StyledTooltip
            title={intl.formatMessage({
              id: 'disponibilidadActiva.motivo.tooltipPlazoVencido',
              defaultMessage: 'El plazo de 2 días para editar el motivo de esta ociosidad venció.',
            })}
            arrow
            placement="top"
          >
            <i className="fa fa-lock" style={{ marginRight: '5px', marginLeft: '5px', color: '#999' }} />
          </StyledTooltip>
        )}
        {editable && controlEdicion}
      </div>
    );
  };

  // VEC-3894: espejo parcial de dropdownMotivos() — a diferencia del motivo, acá la única
  // condición de edición es el permiso (§2.7 del análisis): sin flag de estado de período
  // (abierto/cerrado) y sin ninguna ventana de tiempo tipo PlazoMotivoOciosidad. Con permiso,
  // el datepicker se muestra siempre, cualquiera sea el estado del período.
  const fechaRetornoEditable = () => {
    const tienePermiso = Security.hasPermission('DISPONIBILIDAD_ACTIVA_ASIGNAR_MOTIVOS');

    if (!tienePermiso) {
      return movil.fechaRetorno;
    }

    return (
      <DatePicker
        id={`fechaRetorno-${movil.id}`}
        name={`fechaRetorno-${movil.id}`}
        className="form-control date-picker-placeholder"
        placeholderText={intl.formatMessage({
          id: 'movilesAbm.render.general_data.placeholder_fecha_de_alta',
          defaultMessage: 'DD/MM/AAAA',
        })}
        selected={movil.fechaRetorno ? moment(movil.fechaRetorno) : null}
        onChange={handleChangeFechaRetorno}
        minDate={moment(movil.fechaInicioOciosidad)}
        onKeyDown={(e) => e.preventDefault()}
        isClearable
        showMonthDropdown
        showYearDropdown
        dropdownMode="select"
        dateFormat={LocaleFormat.getDatePickerFormat()}
      />
    );
  };

  return (
    <TableRow style={seleccionado ? { backgroundColor: '#eefafa' } : undefined}>
      {/* VEC-4367: la columna existe solo si el grid habilita la selección (permiso de asignar motivos). */}
      {onToggleSeleccion && (
        <StyledTableCell align="left">
          <input
            type="checkbox"
            checked={!!seleccionado}
            disabled={!seleccionable}
            style={{ cursor: seleccionable ? 'pointer' : 'not-allowed' }}
            onChange={() => onToggleSeleccion(movil.id)}
            aria-label={`Seleccionar ${movil.dominio}`}
          />
        </StyledTableCell>
      )}

      <StyledTableCell align="left">
        {movil.dominio}
      </StyledTableCell>

      <StyledTableCell align="left">
        {movil.fecha_actualizacion_motivo && (
          <div className="text-center success dt-info-icon">
            <StyledTooltip
              title={
                <div>
                  Ult. actualización Motivo: <br />
                  {Timezone.getDateForClient(movil.fecha_actualizacion_motivo, null, 'YYYY-MM-DD HH:mm')} <br />
                  Usuario: {movil.usuarioModificacionMotivo || ''}
                </div>
              }
              arrow
              placement="top"
            >
              <i className="la la-clock-o" />
            </StyledTooltip>
          </div>
        )}
      </StyledTableCell>

      <StyledTableCell align="left">
        {dropdownMotivos()}
      </StyledTableCell>

      <StyledTableCell align="left">
        {fechaRetornoEditable()}
      </StyledTableCell>

      <StyledTableCell align="left">
        {movil.modelo}
      </StyledTableCell>

      <StyledTableCell align="left">
        {movil.tipo}
      </StyledTableCell>

      <StyledTableCell align="left">
        {movil.base}
      </StyledTableCell>

      <StyledTableCell align="left">
        {movil.subRegion}
      </StyledTableCell>

      <StyledTableCell align="left">
        {movil.region}
      </StyledTableCell>

      <StyledTableCell show={columnsEnabled && columnsEnabled.transportadora} align="left">
        {movil.transportadora}
      </StyledTableCell>

      <StyledTableCell show={columnsEnabled && columnsEnabled.rentadora} align="left">
        {movil.rentadora}
      </StyledTableCell>

      <StyledTableCell align="left">
        {movil.centroCostos}
      </StyledTableCell>

      <StyledTableCell align="left">
        {movil.es_reserva
          ? intl.formatMessage({ id: 'Si', defaultMessage: 'Si' })
          : intl.formatMessage({ id: 'No', defaultMessage: 'No' })}
      </StyledTableCell>

      <StyledTableCell align="left">
        {movil.fechaInicioOciosidad}
      </StyledTableCell>

      <StyledTableCell align="left">
        {movil.fechaEventoOciosidad}
      </StyledTableCell>
    </TableRow>
  );
};

export default DisponibilidadActivaGridRow;
